import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { resolveMailLanguage } from "../../emailLanguage";
import { withEntryQuery } from "../../entryParams";
import { CANCELLATION_EMAIL } from "../../externalLinks";
import { formatDateAndTimeInBerlin } from "../../format";
import { ApiFailure } from "../../restClient";
import { WITHDRAWAL_CONFIRM_PATH, WITHDRAWAL_PATH } from "../../routes";
import WithdrawalFailureNotice from "./WithdrawalFailureNotice";
import { PROBLEM_FIELDS, confirmWithdrawal } from "./withdrawalApi";
import { WithdrawalNavState, readNavState } from "./withdrawalFlow";
import "../public-page.css";

/**
 * Step two of the withdrawal function of § 356a BGB: the declaration shown back
 * as it will be recorded, and the "Widerruf bestätigen" button.
 *
 * ⚠️ The withdrawal is given on submit. The mail that follows is the receipt of
 * § 356a Abs. 2, not a step of the process, and the result screen states date
 * and time of receipt because the law wants both confirmed.
 *
 * Reached only from the form; opened directly (bookmark, new tab) it has no
 * declaration to confirm and sends the person back to the form.
 */
function WithdrawalConfirmPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { values, preview, receipt } = readNavState(location.state);

  const locale = i18n.language || "de";
  const language = resolveMailLanguage(locale);

  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  /** The address a withdrawal can always be sent to instead. */
  const contactEmail =
    (preview && preview.contactEmail) ||
    (failure && failure.body && failure.body.contactEmail) ||
    CANCELLATION_EMAIL;

  if (!values || (!preview && !receipt)) {
    const state: WithdrawalNavState = { openForm: true };
    return <Navigate to={withEntryQuery(WITHDRAWAL_PATH)} state={state} replace />;
  }

  /** Back to the form, filled in, optionally with the fields to fix marked. */
  const backToForm = (extra: WithdrawalNavState = {}) => {
    const state: WithdrawalNavState = { values, openForm: true, ...extra };
    navigate(withEntryQuery(WITHDRAWAL_PATH), { state });
  };

  /** Submits the declaration. This IS the withdrawal. */
  const submit = async () => {
    setBusy(true);
    setFailure(null);

    const result = await confirmWithdrawal(values, language);

    setBusy(false);

    if (!result.ok) {
      const problem = result.body && result.body.problem;
      const field = problem ? PROBLEM_FIELDS[problem] : undefined;

      if (field) {
        backToForm({ fieldErrors: [field], failure: result });
        return;
      }

      setFailure(result);
      return;
    }

    // Replaces the history entry, so a reload shows this receipt again and
    // never the button a second time.
    const state: WithdrawalNavState = { values, preview, receipt: result.data };
    navigate(withEntryQuery(WITHDRAWAL_CONFIRM_PATH), { state, replace: true });
  };

  if (receipt) {
    const { date, time } = formatDateAndTimeInBerlin(
      receipt.receivedAt,
      locale,
    );

    return (
      <div className="pub-page">
        <h1 className="pub-title">{t("withdrawal.done.title")}</h1>

        <div className="pub-notice pub-notice-success" role="status">
          <p>{t("withdrawal.done.received", { date, time })}</p>
          <p>{t("withdrawal.done.reference", { reference: receipt.requestId })}</p>
        </div>

        <section className="pub-panel" aria-label={t("withdrawal.done.title")}>
          <p className="pub-text">
            {t("withdrawal.done.mail", { email: values.email.trim() })}
          </p>
          <p className="pub-text">{t("withdrawal.done.refund")}</p>
          <p className="pub-text">
            {t("withdrawal.done.questions")}{" "}
            <a href={`mailto:${contactEmail}`} className="pub-link">
              {contactEmail}
            </a>
          </p>
        </section>

        <button
          type="button"
          className="pub-secondary-btn"
          onClick={() => navigate(withEntryQuery(WITHDRAWAL_PATH))}
        >
          {t("withdrawal.done.back")}
        </button>
      </div>
    );
  }

  const declaration = preview ? preview.declaration : null;

  return (
    <div className="pub-page">
      <button
        type="button"
        className="pub-back-link"
        onClick={() => backToForm()}
      >
        {t("withdrawal.confirm.back")}
      </button>

      <h1 className="pub-title">{t("withdrawal.confirm.title")}</h1>
      <p className="pub-lead">{t("withdrawal.confirm.lead")}</p>

      <WithdrawalFailureNotice failure={failure} contactEmail={contactEmail} />

      <section
        className="pub-panel"
        aria-label={t("withdrawal.confirm.recordedTitle")}
      >
        <h2 className="pub-panel-title">
          {t("withdrawal.confirm.recordedTitle")}
        </h2>
        {/*
          The declaration exactly as the backend will record it, so the person
          confirms the wording and not what he remembers typing.
        */}
        <dl className="pub-facts">
          <div className="pub-fact">
            <dt>{t("withdrawal.confirm.fields.declaration")}</dt>
            <dd>{t("withdrawal.confirm.declarationText")}</dd>
          </div>
          <div className="pub-fact">
            <dt>{t("withdrawal.form.fields.name")}</dt>
            <dd>{declaration && declaration.name}</dd>
          </div>
          <div className="pub-fact">
            <dt>{t("withdrawal.form.fields.email")}</dt>
            <dd>{declaration && declaration.email}</dd>
          </div>
          <div className="pub-fact">
            <dt>{t("withdrawal.form.fields.contractReference")}</dt>
            <dd>{declaration && declaration.contractReference}</dd>
          </div>
          <div className="pub-fact">
            <dt>{t("withdrawal.form.fields.note")}</dt>
            <dd className="pub-fact-multiline">
              {(declaration && declaration.note) ||
                t("withdrawal.confirm.notStated")}
            </dd>
          </div>
        </dl>
      </section>

      <section
        className="pub-panel"
        aria-label={t("withdrawal.confirm.submitAria")}
      >
        {/*
          Mandatory content: the button IS the withdrawal (§ 356a Abs. 1 BGB).
          The label "Widerruf bestätigen" is the one the statute names; keep it.
        */}
        <p className="pub-text">
          {preview && preview.effectiveOnSubmit
            ? t("withdrawal.confirm.effectiveOnSubmit")
            : t("withdrawal.confirm.submitNote")}
        </p>

        <button
          type="button"
          className="pub-primary-btn pub-primary-btn-block"
          disabled={busy}
          onClick={submit}
        >
          {busy
            ? t("withdrawal.confirm.submitting")
            : t("withdrawal.confirm.submitButton")}
        </button>
        <p className="pub-submit-hint">{t("withdrawal.confirm.submitHint")}</p>
      </section>
    </div>
  );
}

export default WithdrawalConfirmPage;
